import { useState, useEffect } from 'react';
import { collection, doc, getDoc, getDocs, onSnapshot, query, where, QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { Church } from '../models/Church';

interface UseChurchesProps {
  userId?: string; // ← auth.currentUser?.uid
  includeAll?: boolean;
}

interface UseChurchesReturn {
  churches: Church[];
  loading: boolean;
  error: string;
}

export const useChurches = ({ userId, includeAll = false }: UseChurchesProps): UseChurchesReturn => {
  const [churches, setChurches] = useState<Church[]>([]);
  const [loading, setLoading] = useState(!!userId); // start true if userId exists
  const [error, setError] = useState('');
  const [loadedUserId, setLoadedUserId] = useState<string>();

  useEffect(() => {
    // Wipe state whenever the UID disappears
    if (!userId) {
      setChurches([]);
      setLoading(false);
      setLoadedUserId(undefined);
      return;
    }

    // A user or role change starts a different query. Do not leave the prior
    // query's cards visible while its replacement is still loading.
    setChurches([]);
    setError('');
    setLoading(true);

    let active = true;
    let latestSnapshot = 0;

    const setUniqueChurches = (churchList: Church[]) => {
      // Membership queries can overlap (an administrator may also be a
      // transcript viewer). Keep the UI's source of truth unique even if a
      // query is updated while Firestore is catching up.
      const uniqueChurches = Array.from(
        new Map(churchList.map((church) => [church.id, church])).values(),
      );
      uniqueChurches.sort((a, b) => a.name.localeCompare(b.name));
      setChurches(uniqueChurches);
    };

    if (includeAll) {
      const unsubscribe = onSnapshot(
        query(collection(db, 'churches')),
        (snapshot) => {
          if (!active) return;
          const list = snapshot.docs.map((churchDoc) => ({
            ...(churchDoc.data() as Church),
            id: churchDoc.id,
          }));
          setUniqueChurches(list);
          setLoading(false);
          setLoadedUserId(userId);
        },
        (nextError) => {
          if (!active) return;
          setError(nextError.message);
          setLoading(false);
          setLoadedUserId(userId);
        },
      );
      return () => {
        active = false;
        unsubscribe();
      };
    }

    const unsub = onSnapshot(
      query(collection(db, 'settings'), where('admins', 'array-contains', userId)),
      async (adminSettingsSnap) => {
        // onSnapshot does not cancel work already awaited inside a previous
        // callback. Only the newest snapshot from the active subscription may
        // update state.
        const snapshotVersion = ++latestSnapshot;
        let viewerSettingsDocs: QueryDocumentSnapshot[] = [];
        try {
          const viewerSettingsSnap = await getDocs(
            query(collection(db, 'settings'), where('transcript_viewers', 'array-contains', userId)),
          );
          viewerSettingsDocs = viewerSettingsSnap.docs;
        } catch (err) {
          console.warn('Could not load transcript viewer churches:', err);
        }
        if (!active || snapshotVersion !== latestSnapshot) return;

        const ids = Array.from(
          new Set([...adminSettingsSnap.docs, ...viewerSettingsDocs].map((d) => d.id)),
        );
        if (ids.length === 0) {
          setChurches([]);
          setLoading(false);
          setLoadedUserId(userId);
          return;
        }

        // Fetch each known church directly. Firestore evaluates these as `get`
        // operations, so a church admin never needs permission to list the
        // complete churches collection.
        const churchSnaps = await Promise.all(ids.map((id) => getDoc(doc(db, 'churches', id))));
        if (!active || snapshotVersion !== latestSnapshot) return;

        const list = churchSnaps
          .filter((churchSnap) => churchSnap.exists())
          .map((churchSnap) => ({ ...(churchSnap.data() as Church), id: churchSnap.id }));

        setUniqueChurches(list);
        setLoading(false);
        setLoadedUserId(userId);
      },
      (err) => {
        if (!active) return;
        setError(err.message);
        setLoading(false);
        setLoadedUserId(userId);
      },
    );

    return () => {
      active = false;
      unsub();
    };
  }, [includeAll, userId]);

  return { churches, loading: loading || (!!userId && loadedUserId !== userId), error };
};
